// ======================================================
// FILE: src/components/AdvanceHealthcareDirective.js
// ======================================================
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import { db, storage } from "../firebase";
import {
  doc,
  getDoc,
  onSnapshot,
  setDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { FiEye } from "react-icons/fi";
import { FaYoutube } from "react-icons/fa";
import { useAccess } from "../access/AccessContext";
import PageScaffold from "./ui/PageScaffold";
import VideoLightbox from "./ui/VideoLightbox";
import "../styles/layout.css";

// Helpers
function toStoragePath(maybe) {
  if (!maybe || typeof maybe !== "string") return null;
  if (maybe.startsWith("gs://")) {
    const no = maybe.replace(/^gs:\/\//, "");
    const slash = no.indexOf("/");
    return slash >= 0 ? no.slice(slash + 1) : null;
  }
  if (maybe.startsWith("http")) {
    const m = maybe.match(/\/o\/([^?]+)/);
    if (!m) return null;
    try { return decodeURIComponent(m[1]); } catch { return m[1]; }
  }
  if (maybe.includes("..")) return null;
  return maybe;
}
async function getSignedDownloadUrl({ pathOrUrl }) {
  try {
    if (!pathOrUrl) return null;
    if (pathOrUrl.startsWith("http")) return pathOrUrl;
    const u = await getDownloadURL(storageRef(storage, pathOrUrl));
    return u;
  } catch {
    return null;
  }
}
function downloadBlobUrl(url, filename) {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "document";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export default function AdvanceHealthcareDirective({ user }) {
  const { readOnly } = useAccess() || { readOnly: false };

  // Remote state
  const [loading, setLoading] = useState(true);
  const [docState, setDocState] = useState(null);

  // Core fields
  const [subject, setSubject] = useState("");
  const [fullName, setFullName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [proxyName, setProxyName] = useState("");      // stored keys unchanged
  const [proxyPhone, setProxyPhone] = useState("");
  const [proxyEmail, setProxyEmail] = useState("");
  const [dnr, setDnr] = useState(false);
  const [ventilation, setVentilation] = useState(false);
  const [nutrition, setNutrition] = useState(false);
  const [hydration, setHydration] = useState(false);
  const [notes, setNotes] = useState("");

  // Optional Witness & GP (new)
  const [w1Name, setW1Name] = useState("");
  const [w1Signature, setW1Signature] = useState("");
  const [w1Date, setW1Date] = useState("");
  const [w2Name, setW2Name] = useState("");
  const [w2Signature, setW2Signature] = useState("");
  const [w2Date, setW2Date] = useState("");
  const [gpName, setGpName] = useState("");
  const [gpContact, setGpContact] = useState("");
  const [gpSignature, setGpSignature] = useState("");

  // File (PDF/image)
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [documentUrl, setDocumentUrl] = useState("");
  const [documentName, setDocumentName] = useState("");
  const fileInput = useRef(null);

  // Help / video
  const [showDrawer, setShowDrawer] = useState(false);
  const [showVideo, setShowVideo] = useState(false);

  // UX feedback
  const [msgOk, setMsgOk] = useState("");
  const [msgErr, setMsgErr] = useState("");

  // Load doc
  useEffect(() => {
    if (!user?.uid) return;
    setLoading(true);
    const ref = doc(db, "advanceHealthcareDirectives", user.uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setDocState(null);
          setLoading(false);
          return;
        }
        const d = snap.data();
        setDocState(d);

        setSubject(d.subject || "");
        setFullName(d.fullName || "");
        setDateOfBirth(d.dateOfBirth || "");
        setProxyName(d.proxyName || "");
        setProxyPhone(d.proxyPhone || "");
        setProxyEmail(d.proxyEmail || "");
        setDnr(Boolean(d.dnr));
        setVentilation(Boolean(d.ventilation));
        setNutrition(Boolean(d.nutrition));
        setHydration(Boolean(d.hydration));
        setNotes(d.notes || "");
        setDocumentUrl(d.documentUrl || "");
        setDocumentName(d.documentName || "");

        // hydrate optional
        setW1Name(d.w1Name || "");
        setW1Signature(d.w1Signature || "");
        setW1Date(d.w1Date || "");
        setW2Name(d.w2Name || "");
        setW2Signature(d.w2Signature || "");
        setW2Date(d.w2Date || "");
        setGpName(d.gpName || "");
        setGpContact(d.gpContact || "");
        setGpSignature(d.gpSignature || "");

        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsub();
  }, [user?.uid]);

  function resetLocalFile() {
    setFile(null);
    if (fileInput.current) fileInput.current.value = "";
  }
  function handleLocalFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
  }

  // Validation (minimal)
  function validate() {
    const errors = {};
    if (!fullName.trim()) errors.fullName = "Full name is required.";
    return errors;
  }
  const errors = validate();
  const isValid = Object.keys(errors).length === 0;

  async function uploadIfNeeded() {
    if (!file) return { url: documentUrl, name: documentName };
    const ext = (file.name.split(".").pop() || "pdf").toLowerCase();
    const safe = `${user.uid}_${Date.now()}.${ext}`;
    const path = `advanceHealthcareDirectives/${user.uid}/${safe}`;
    const sRef = storageRef(storage, path);
    setUploading(true);
    try {
      const task = uploadBytesResumable(sRef, file);
      await new Promise((res, rej) => task.on("state_changed", null, rej, res));
      const url = await getDownloadURL(task.snapshot.ref);
      return { url, name: safe };
    } finally {
      setUploading(false);
    }
  }

  async function handleSave(e) {
    e?.preventDefault?.();
    setMsgOk(""); setMsgErr("");
    if (readOnly) return;
    if (!isValid) { setMsgErr("Please fix the highlighted fields."); return; }

    try {
      const { url, name } = await uploadIfNeeded();
      await setDoc(
        doc(db, "advanceHealthcareDirectives", user.uid),
        {
          uid: user.uid,
          subject: (subject || "").trim(),
          fullName: (fullName || "").trim(),
          dateOfBirth: (dateOfBirth || "").trim(),
          proxyName: (proxyName || "").trim(),
          proxyPhone: (proxyPhone || "").trim(),
          proxyEmail: (proxyEmail || "").trim(),
          dnr: !!dnr,
          ventilation: !!ventilation,
          nutrition: !!nutrition,
          hydration: !!hydration,
          notes: (notes || "").trim(),
          documentUrl: url || "",
          documentName: name || documentName || "",
          // NEW: optional witness & GP fields
          w1Name: (w1Name || "").trim(),
          w1Signature: (w1Signature || "").trim(),
          w1Date: (w1Date || "").trim(),
          w2Name: (w2Name || "").trim(),
          w2Signature: (w2Signature || "").trim(),
          w2Date: (w2Date || "").trim(),
          gpName: (gpName || "").trim(),
          gpContact: (gpContact || "").trim(),
          gpSignature: (gpSignature || "").trim(),
          updated: serverTimestamp(),
        },
        { merge: true }
      );
      setDocumentUrl(url || "");
      setDocumentName(name || documentName || "");
      resetLocalFile();
      setMsgOk("Advance Healthcare Directive saved.");
    } catch (err) {
      setMsgErr(err?.message || "Failed to save. Please try again.");
    }
  }

  async function handleDelete() {
    if (readOnly) return;
    if (!window.confirm("Delete your Advance Healthcare Directive?")) return;
    setMsgOk(""); setMsgErr("");
    try {
      const refDoc = doc(db, "advanceHealthcareDirectives", user.uid);
      const cur = (await getDoc(refDoc)).data();
      if (cur?.documentUrl) {
        const enc = cur.documentUrl.split("/o/")[1]?.split("?")[0];
        const path = enc ? decodeURIComponent(enc) : null;
        if (path) { try { await deleteObject(storageRef(storage, path)); } catch {} }
      }
      await deleteDoc(refDoc);

      setDocState(null);
      resetLocalFile();
      setSubject("");
      setFullName("");
      setDateOfBirth("");
      setProxyName("");
      setProxyPhone("");
      setProxyEmail("");
      setDnr(false);
      setVentilation(false);
      setNutrition(false);
      setHydration(false);
      setNotes("");
      setDocumentUrl("");
      setDocumentName("");
      setW1Name(""); setW1Signature(""); setW1Date("");
      setW2Name(""); setW2Signature(""); setW2Date("");
      setGpName(""); setGpContact(""); setGpSignature("");
      setMsgOk("Deleted.");
    } catch (err) {
      setMsgErr(err?.message || "Failed to delete.");
    }
  }

  async function handleDownload() {
    const base = toStoragePath(documentUrl) || documentUrl;
    if (!base) return;
    const url = await getSignedDownloadUrl({ pathOrUrl: base });
    if (!url) return alert("Could not generate a download link.");
    const suggested = (subject?.trim() ? subject.trim().replace(/[^\w.-]+/g, "_") : "AHD") + (url.includes(".pdf") ? ".pdf" : "");
    downloadBlobUrl(url, suggested || "AHD.pdf");
  }

  function handlePrint() { try { window.print(); } catch {} }

  if (loading) return <div className="empty-state" style={{ padding: 32 }}>Loading…</div>;

  const actionsDesktop = (
    <>
      <button className="btn dp-help read" onClick={() => setShowDrawer(true)} data-allow-readonly>
        <FiEye className="dp-help-ico" /> Read how it works
      </button>
      <button className="btn dp-help see" onClick={() => setShowVideo(true)} data-allow-readonly>
        <FaYoutube className="dp-help-ico" /> See how it works
      </button>
    </>
  );
  const actionsMobile = (
    <div className="dp-help-row">
      <button className="btn dp-help read" onClick={() => setShowDrawer(true)} data-allow-readonly>
        <FiEye className="dp-help-ico" /> Read how it works
      </button>
      <button className="btn dp-help see" onClick={() => setShowVideo(true)} data-allow-readonly>
        <FaYoutube className="dp-help-ico" /> See how it works
      </button>
    </div>
  );

  const showTile = Boolean(docState);

  return (
    <>
      <PageScaffold
        title="Advance Healthcare Directive"
        onAdd={undefined}
        heroSrc="/lastwish-advanced-healthcare.jpg"
        heroAlt="Advance Healthcare Directive"
        actionsDesktop={actionsDesktop}
        actionsMobile={actionsMobile}
        
      >
        {showTile && (
          <div className="card dv-item" style={{ marginBottom: 16 }}>
            <div className="dv-left">
              <div className="tile-thumb" style={{ display: "grid", placeItems: "center", background: "#f3f3f3" }}>🏥</div>
            </div>
            <div className="dv-main">
              <div className="tile-title">{subject?.trim() || "Advance Healthcare Directive"}</div>
              <div className="tile-sub">
                {fullName ? `${fullName}` : "No name set"}
                {dateOfBirth ? ` · DOB ${dateOfBirth}` : ""}
                {dnr ? " · DNR" : ""}
                {documentUrl ? " · Document attached" : ""}
              </div>
            </div>
            <div className="dv-actions">
              <button className="btn btn-main" onClick={handlePrint} data-allow-readonly>Print summary</button>
              <button className="btn btn-main" onClick={handlePrint} data-allow-readonly>Download as PDF</button>
              {documentUrl && (
                <button className="btn btn-main" onClick={handleDownload} data-allow-readonly>Download</button>
              )}
              {!readOnly && (
                <button className="btn btn-danger" onClick={handleDelete}>Delete</button>
              )}
            </div>
          </div>
        )}

        <form className="ahd-card" onSubmit={handleSave} noValidate>
            {msgOk && <div className="pf-alert pf-alert--ok" style={{ marginBottom: 10 }}>{msgOk}</div>}
            {msgErr && <div className="pf-alert pf-alert--err" style={{ marginBottom: 10 }}>{msgErr}</div>}

            <div className="ahd-grid">
              <div className="ahd-field">
                <label className="ahd-label">Title (optional)</label>
                <input className="ahd-input" value={subject} onChange={(e)=>setSubject(e.target.value)} placeholder="e.g., My AHD" disabled={readOnly} />
              </div>

              <div className="ahd-field">
                <label className="ahd-label">Your full name <span style={{ color: "#b40000" }}>*</span></label>
                <input
                  className={"ahd-input" + (errors.fullName ? " ahd-input--err" : "")}
                  value={fullName}
                  onChange={(e)=>setFullName(e.target.value)}
                  placeholder="Enter your full name"
                  required
                  disabled={readOnly}
                />
                {errors.fullName && <div className="ahd-err">{errors.fullName}</div>}
              </div>

              <div className="ahd-field">
                <label className="ahd-label">Date of birth</label>
                <input type="date" className="ahd-input" value={dateOfBirth} onChange={(e)=>setDateOfBirth(e.target.value)} disabled={readOnly} />
              </div>

              <div className="ahd-field">
                <label className="ahd-label">Trusted decision-maker (name)</label>
                <input className="ahd-input" value={proxyName} onChange={(e)=>setProxyName(e.target.value)} placeholder="Who should make decisions for you?" disabled={readOnly} />
              </div>

              <div className="ahd-field">
                <label className="ahd-label">Decision-maker phone</label>
                <input className="ahd-input" value={proxyPhone} onChange={(e)=>setProxyPhone(e.target.value)} placeholder="+353 ..." disabled={readOnly} />
              </div>

              <div className="ahd-field">
                <label className="ahd-label">Decision-maker email</label>
                <input type="email" className="ahd-input" value={proxyEmail} onChange={(e)=>setProxyEmail(e.target.value)} placeholder="name@example.com" disabled={readOnly} />
              </div>
            </div>

            <div className="ahd-slab">
              <div className="ahd-slab-title">Care preferences</div>
              <div className="ahd-grid">
                <label className="ahd-check">
                  <input type="checkbox" checked={dnr} onChange={(e)=>setDnr(e.target.checked)} disabled={readOnly} />
                  <span>Do not resuscitate (DNR)</span>
                </label>
                <label className="ahd-check">
                  <input type="checkbox" checked={ventilation} onChange={(e)=>setVentilation(e.target.checked)} disabled={readOnly} />
                  <span>Mechanical ventilation acceptable</span>
                </label>
                <label className="ahd-check">
                  <input type="checkbox" checked={nutrition} onChange={(e)=>setNutrition(e.target.checked)} disabled={readOnly} />
                  <span>Artificial nutrition acceptable</span>
                </label>
                <label className="ahd-check">
                  <input type="checkbox" checked={hydration} onChange={(e)=>setHydration(e.target.checked)} disabled={readOnly} />
                  <span>Artificial hydration acceptable</span>
                </label>
              </div>

              <div className="ahd-field" style={{ marginTop: 8 }}>
                <label className="ahd-label">Additional instructions</label>
                <textarea className="ahd-input ahd-textarea" rows={6} value={notes} onChange={(e)=>setNotes(e.target.value)} placeholder="Any additional wishes or limitations…" disabled={readOnly} />
              </div>
            </div>

            {/* NEW: Optional Witness & GP fields */}
            <div className="ahd-slab">
              <div className="ahd-slab-title">Witness & GP details (optional)</div>
              <div className="ahd-grid">
                <div className="ahd-field">
                  <label className="ahd-label">Witness 1 – Name</label>
                  <input className="ahd-input" value={w1Name} onChange={(e)=>setW1Name(e.target.value)} placeholder="Name of witness 1" disabled={readOnly} />
                </div>
                <div className="ahd-field">
                  <label className="ahd-label">Witness 1 – Signature (typed)</label>
                  <input className="ahd-input" value={w1Signature} onChange={(e)=>setW1Signature(e.target.value)} placeholder="Type signature or leave blank" disabled={readOnly} />
                </div>
                <div className="ahd-field">
                  <label className="ahd-label">Witness 1 – Date</label>
                  <input type="date" className="ahd-input" value={w1Date} onChange={(e)=>setW1Date(e.target.value)} disabled={readOnly} />
                </div>

                <div className="ahd-field">
                  <label className="ahd-label">Witness 2 – Name</label>
                  <input className="ahd-input" value={w2Name} onChange={(e)=>setW2Name(e.target.value)} placeholder="Name of witness 2" disabled={readOnly} />
                </div>
                <div className="ahd-field">
                  <label className="ahd-label">Witness 2 – Signature (typed)</label>
                  <input className="ahd-input" value={w2Signature} onChange={(e)=>setW2Signature(e.target.value)} placeholder="Type signature or leave blank" disabled={readOnly} />
                </div>
                <div className="ahd-field">
                  <label className="ahd-label">Witness 2 – Date</label>
                  <input type="date" className="ahd-input" value={w2Date} onChange={(e)=>setW2Date(e.target.value)} disabled={readOnly} />
                </div>

                <div className="ahd-field">
                  <label className="ahd-label">GP / Doctor – Name</label>
                  <input className="ahd-input" value={gpName} onChange={(e)=>setGpName(e.target.value)} placeholder="Doctor's name" disabled={readOnly} />
                </div>
                <div className="ahd-field">
                  <label className="ahd-label">GP / Doctor – Contact</label>
                  <input className="ahd-input" value={gpContact} onChange={(e)=>setGpContact(e.target.value)} placeholder="Phone or email" disabled={readOnly} />
                </div>
                <div className="ahd-field">
                  <label className="ahd-label">GP / Doctor – Signature (typed)</label>
                  <input className="ahd-input" value={gpSignature} onChange={(e)=>setGpSignature(e.target.value)} placeholder="Type signature or leave blank" disabled={readOnly} />
                </div>
              </div>
            </div>

            <div className="ahd-field">
              <label className="ahd-label">Attach signed directive (PDF/image)</label>
              <div className="ahd-row">
                <label className="ahd-file">
                  <input ref={fileInput} type="file" accept=".pdf,image/*" onChange={handleLocalFile} disabled={readOnly} />
                  <span className="ahd-file-btn">{file ? "Change file" : "Choose file"}</span>
                  <span className="ahd-file-hint">{file?.name || documentName || "PDF/JPG/PNG (optional)"}{uploading ? " – Uploading…" : ""}</span>
                </label>
                {documentUrl && (
                  <button type="button" className="btn btn-main" onClick={handleDownload} data-allow-readonly>
                    Download current
                  </button>
                )}
                <button type="button" className="btn btn-main" onClick={handlePrint} data-allow-readonly>
                  Print summary
                </button>
                <button type="button" className="btn btn-main" onClick={handlePrint} data-allow-readonly>
                  Download as PDF
                </button>
              </div>
            </div>

            <div className="ahd-actions">
              <button className="btn btn-main" type="submit" disabled={readOnly || !isValid || uploading}>
                {uploading ? "Uploading…" : "Save"}
              </button>
              {!readOnly && docState && (
                <button
                  className="btn btn-cancel"
                  type="button"
                  onClick={()=>{
                    const d = docState || {};
                    setSubject(d.subject || "");
                    setFullName(d.fullName || "");
                    setDateOfBirth(d.dateOfBirth || "");
                    setProxyName(d.proxyName || "");
                    setProxyPhone(d.proxyPhone || "");
                    setProxyEmail(d.proxyEmail || "");
                    setDnr(Boolean(d.dnr));
                    setVentilation(Boolean(d.ventilation));
                    setNutrition(Boolean(d.nutrition));
                    setHydration(Boolean(d.hydration));
                    setNotes(d.notes || "");
                    setDocumentUrl(d.documentUrl || "");
                    setDocumentName(d.documentName || "");
                    setW1Name(d.w1Name || ""); setW1Signature(d.w1Signature || ""); setW1Date(d.w1Date || "");
                    setW2Name(d.w2Name || ""); setW2Signature(d.w2Signature || ""); setW2Date(d.w2Date || "");
                    setGpName(d.gpName || ""); setGpContact(d.gpContact || ""); setGpSignature(d.gpSignature || "");
                    resetLocalFile();
                    setMsgOk(""); setMsgErr("");
                  }}
                >
                  Cancel
                </button>
              )}
              {!readOnly && docState && (
                <button className="btn btn-danger" type="button" onClick={handleDelete}>Delete</button>
              )}
            </div>
            )}
          </form>
      </PageScaffold>

      {/* PRINT SUMMARY */}
      <div className="ahd-print" aria-hidden="true">
        <div className="ahd-print-wrap">
          <div className="ahd-print-head">
            <div className="ahd-print-title">Advance Healthcare Directive</div>
            <div className="ahd-print-meta">
              <div>{new Date().toLocaleString()}</div>
              {user?.email ? <div>{user.email}</div> : null}
            </div>
          </div>

          <div className="ahd-print-grid">
            <div className="ahd-print-row"><b>Title:</b> <span>{subject || "-"}</span></div>
            <div className="ahd-print-row"><b>Your full name:</b> <span>{fullName || "-"}</span></div>
            <div className="ahd-print-row"><b>Date of birth:</b> <span>{dateOfBirth || "-"}</span></div>

            <div className="ahd-print-row"><b>Trusted decision-maker (name):</b> <span>{proxyName || "-"}</span></div>
            <div className="ahd-print-row"><b>Decision-maker phone:</b> <span>{proxyPhone || "-"}</span></div>
            <div className="ahd-print-row"><b>Decision-maker email:</b> <span>{proxyEmail || "-"}</span></div>

            <div className="ahd-print-row"><b>Do not resuscitate (DNR):</b> <span>{dnr ? "Yes" : "No"}</span></div>
            <div className="ahd-print-row"><b>Mechanical ventilation acceptable:</b> <span>{ventilation ? "Yes" : "No"}</span></div>
            <div className="ahd-print-row"><b>Artificial nutrition acceptable:</b> <span>{nutrition ? "Yes" : "No"}</span></div>
            <div className="ahd-print-row"><b>Artificial hydration acceptable:</b> <span>{hydration ? "Yes" : "No"}</span></div>

            <div className="ahd-print-row ahd-print-notes">
              <b>Additional instructions:</b>
              <div className="ahd-print-notes-box">{(notes || "-")}</div>
            </div>

            <div className="ahd-print-row"><b>Attached document:</b> <span>{documentUrl ? (documentName || "Attached") : "None"}</span></div>

            <div className="ahd-print-slab">
              <div className="ahd-print-slab-title">Witness & GP Details (for signing)</div>

              <div className="ahd-print-row"><b>Witness 1 – Name:</b> <span>{w1Name || "____________________________"}</span></div>
              <div className="ahd-print-row"><b>Witness 1 – Signature:</b> <span>{w1Signature || "____________________________"}</span></div>
              <div className="ahd-print-row"><b>Witness 1 – Date:</b> <span>{w1Date || "____________________"}</span></div>

              <div className="ahd-print-row"><b>Witness 2 – Name:</b> <span>{w2Name || "____________________________"}</span></div>
              <div className="ahd-print-row"><b>Witness 2 – Signature:</b> <span>{w2Signature || "____________________________"}</span></div>
              <div className="ahd-print-row"><b>Witness 2 – Date:</b> <span>{w2Date || "____________________"}</span></div>

              <div className="ahd-print-row"><b>GP / Doctor – Name:</b> <span>{gpName || "____________________________"}</span></div>
              <div className="ahd-print-row"><b>GP / Doctor – Contact:</b> <span>{gpContact || "____________________________"}</span></div>
              <div className="ahd-print-row"><b>GP / Doctor – Signature / Stamp:</b> <span>{gpSignature || "____________________________"}</span></div>
            </div>
          </div>

          <div className="ahd-print-foot">
            <div>Signature: ______________________________</div>
            <div>Date: ____________________</div>
          </div>
        </div>
      </div>

      <VideoLightbox
        open={showVideo}
        onClose={() => setShowVideo(false)}
        url="https://youtu.be/0Me0vVvofYI"
        title="Advance Healthcare Directive - Overview"
        startAt={0}
        captions={false}
        startMuted={false}
      />

      {showDrawer && (
        <div className="dp-drawer-bg" onClick={() => setShowDrawer(false)}>
          <aside className="dp-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="dp-drawer-head">
              <div className="dp-drawer-title">About Advance Healthcare Directives</div>
              <div className="dp-drawer-tools">
                <button className="btn btn-main" onClick={() => window.print()} type="button" data-allow-readonly>Print</button>
                <button className="btn btn-cancel" onClick={() => setShowDrawer(false)} type="button" data-allow-readonly>Close</button>
              </div>
            </div>
            <div className="dp-drawer-body">
              <p><b>What is an AHD?</b> It records your preferences for medical care if you cannot communicate, and names someone you trust to decide for you.</p>
              <ol>
                <li>Fill in your details and preferences.</li>
                <li>Optionally upload a signed PDF or image of your directive.</li>
                <li>Save. You can update or remove it any time.</li>
                <li>Managers can view this page but cannot edit it.</li>
              </ol>
            </div>
          </aside>
        </div>
      )}

      <style>{`
        .ahd-card { border:1px solid #e5e7eb; border-radius:14px; background:#fff; padding:16px; }
        .ahd-grid { display:grid; grid-template-columns:1fr; gap:14px; }
        @media (min-width: 720px) { .ahd-grid { grid-template-columns:1fr 1fr; } }
        .ahd-field { display:flex; flex-direction:column; gap:8px; margin-bottom:12px; }
        .ahd-label { color:#374151; font-size:13px; font-weight:600; }
        .ahd-input { width:100%; min-height:42px; border:1px solid #d6d6e7; border-radius:10px; padding:10px 12px; }
        .ahd-input--err { border-color:#ef4444; box-shadow:0 0 0 3px rgba(239,68,68,.12); }
        .ahd-err { color:#991b1b; font-size:12px; margin-top:-4px; }
        .ahd-textarea { min-height:140px; resize:vertical; line-height:1.5; }
        .ahd-row { display:flex; gap:10px; align-items:center; flex-wrap:wrap; }
        .ahd-file { position:relative; display:inline-flex; align-items:center; gap:10px; }
        .ahd-file input[type="file"] { position:absolute; inset:0; opacity:0; cursor:pointer; }
        .ahd-file-btn { display:inline-flex; align-items:center; justify-content:center; height:40px; padding:0 14px; border-radius:10px; background:#003353; color:#fff; font-weight:700; }
        .ahd-file-hint { color:#6b7280; font-size:12px; }
        .ahd-actions { display:flex; gap:10px; flex-wrap:wrap; margin-top:8px; }
        .ahd-slab { border:1px dashed #e5e7eb; border-radius:12px; padding:12px; background:#fafafa; margin:10px 0; }
        .ahd-slab-title { font-weight:700; color:#2a0516; margin-bottom:8px; }
        .ahd-check { display:flex; align-items:center; gap:10px; padding:8px 0; }

        /* Tiles / header helpers */
        .dv-item{display:grid;grid-template-columns:auto 1fr auto;align-items:center;gap:12px;padding:12px;}
        .dv-left{width:42px}
        .tile-thumb{width:42px;height:42px;border-radius:8px;object-fit:cover;background:#f4f5f9;display:block}
        .dv-actions{display:flex;gap:8px;justify-self:end}

        :root { --topbar-h: 56px; --orange:#f15822; --blue:#8cade1; }
        .dp-help-row { display:flex; gap:10px; flex-wrap:wrap; }
        .dp-help { border-radius: 8px; padding: 10px 14px; display: inline-flex; align-items: center; gap: 10px; font-weight: 700; color: #fff; border: none; }
        .dp-help.read { background: #650202; color: #fff; }
        .dp-help.see  { background: #003353; color: #fff; }
        .dp-help-ico { font-size: 1.05rem; }
        .dp-intro { margin: 4px 0 10px; color: #2a0516; }

        /* Drawer */
        .dp-drawer-bg { position:fixed; inset:0; background:rgba(0,0,0,0.3); z-index:10000; display:flex; justify-content:flex-end; }
        .dp-drawer { position: fixed; top: var(--topbar-h, 56px); right: 0; height: calc(100vh - var(--topbar-h, 56px)); width: min(520px, 95vw); background: #fff; box-shadow: -8px 0 24px rgba(0,0,0,0.15); border-top-left-radius: 12px; border-bottom-left-radius: 12px; display: flex; flex-direction: column; overflow: hidden; }
        .dp-drawer-head { display:flex; align-items:center; justify-content:space-between; gap:10px; padding:14px 16px; border-bottom:1px solid #f0f0f0; background:#fafafa; }
        .dp-drawer-title { font-weight:700; color:#2a0516; }
        .dp-drawer-tools { display:flex; gap:8px; }
        .dp-drawer-body { padding:16px; overflow:auto; }
        .pf-alert{padding:10px 12px;border-radius:10px;border:1px solid #e5e7eb;background:#f9fafb}
        .pf-alert--ok{background:#ecfdf5;border-color:#a7f3d0;color:#065f46}
        .pf-alert--err{background:#fee2e2;border-color:#fecaca;color:#7a0b0b}

        /* Print */
        .ahd-print { display:none; }
        @media print {
          body * { visibility:hidden !important; }
          .ahd-print, .ahd-print * { visibility:visible !important; }
          .ahd-print { display:block; position:absolute; left:0; top:0; width:100%; padding:24px; }
          .ahd-print-wrap { max-width: 800px; margin: 0 auto; font-size: 13px; color:#111827; }
          .ahd-print-head { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom: 16px; }
          .ahd-print-title { font-size: 22px; font-weight: 800; }
          .ahd-print-meta { text-align:right; color:#6b7280; }
          .ahd-print-grid { display:grid; grid-template-columns: 1fr; gap: 10px; border-top:1px solid #e5e7eb; padding-top:10px; }
          .ahd-print-row { display:flex; gap:8px; }
          .ahd-print-row b { min-width: 270px; }
          .ahd-print-notes .ahd-print-notes-box {
            white-space: pre-wrap; border:1px solid #e5e7eb; border-radius:8px; padding:10px; background:#fafafa; min-height:90px;
          }
          .ahd-print-slab { margin-top: 14px; border-top: 1px dashed #e5e7eb; padding-top: 12px; }
          .ahd-print-slab-title { font-weight: 800; margin-bottom: 8px; }
          .ahd-print-foot { display:flex; gap:20px; justify-content:flex-start; margin-top: 18px; }
        }
      `}</style>
    </>
  );
}
